import {
  HTMLProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as Styled from '../TableStyled';
import TableConfig from '../TableConfig';
import { useHeaderButtons } from '../../header/HeaderButtonProvider';
import SquareButton from '../../button/SquareButton';
import { useTheme } from 'styled-components';
import { keyMapping } from '../../../api/apiConfig';
import { BsChevronDoubleLeft } from 'react-icons/bs';
import { BsChevronDoubleRight } from 'react-icons/bs';
import { BsChevronLeft } from 'react-icons/bs';
import { BsChevronRight } from 'react-icons/bs';

import { OrderListType } from '../../../api/apiConfig';
import { PaginationState } from '../Pagination';
import {
  formatCurrency,
  formatEightDigitStringToSlashForm,
} from '../../../function/replaceString';

function mapKey(inputKey: string): keyof OrderListType | undefined {
  return keyMapping[inputKey];
}

export const OrderTableWithProps = ({
  data,
  onCheck,
}: {
  data: OrderListType[];
  onCheck?: (props: { ORDER_ID: string; state: boolean }) => void;
}) => {
  const theme = useTheme();
  const { setButtons } = useHeaderButtons();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  //테이블 옵션값 ~~~
  const [headerOverflowType, setHeaderOverflowType] = useState<'wrap' | 'hide'>(
    'hide'
  );
  const [bodyOverflowType, setBodyOverflowType] = useState<'wrap' | 'hide'>(
    'hide'
  );
  const [bodyAlignHoriType, setBodyAlignHoriType] = useState<
    'left' | 'center' | 'right'
  >('left');

  const [cellHoriPaddValue, setCellHoriPaddValue] = useState<number>(5);
  const [bodyVertPaddValue, setBodyVertPaddValue] = useState<number>(5);

  const [showConfig, setShowConfig] = useState<boolean>(false);

  const IndeterminateCheckbox = ({
    indeterminate,
    className = '',
    ...rest
  }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) => {
    const ref = useRef<HTMLInputElement>(null!);

    useEffect(() => {
      if (typeof indeterminate === 'boolean') {
        ref.current.indeterminate = !rest.checked && indeterminate;
      }
    }, [ref, indeterminate]);

    return (
      <input
        type='checkbox'
        ref={ref}
        className={className + ' cursor-pointer'}
        {...rest}
      />
    );
  };

  const columns = useMemo<ColumnDef<OrderListType>[]>(
    () => [
      {
        //TH Cell
        header: ({ table }) => <>No</>,
        cell: ({ row, getValue }) => {
          const originalData = row.original;
          return (
            <Styled.NoBodyCell style={{ paddingLeft: `${row.depth * 2}rem` }}>
              {row.getCanExpand() && (
                <>
                  {onCheck !== undefined && (
                    <>
                      <IndeterminateCheckbox
                        {...{
                          checked: row.getIsSelected(),
                          indeterminate: row.getIsSomeSelected(),
                          onChange: (e) => {
                            // row의 선택 상태를 토글
                            row.getToggleSelectedHandler()(e);

                            // 콜백 함수 호출
                            onCheck({
                              ORDER_ID: originalData.ORDER_ID!,
                              state: !row.getIsSelected(),
                            });
                          },
                        }}
                      />{' '}
                    </>
                  )}
                  <Styled.NoText>{row.index + 1}</Styled.NoText>
                </>
              )}
            </Styled.NoBodyCell>
          );
        },

        id: 'NO',
        size: 79,
      },
      {
        header: '주문번호',
        accessorKey: 'ORDER_ID',
        size: 84,
      },
      {
        header: '거래처명',
        accessorKey: 'CUST_DES',
        size: 180,
      },
      {
        header: '담당자명',
        accessorKey: 'EMP_NAME',
        size: 65,
      },
      {
        header: '품목명',
        accessorKey: 'PROD_DES',
        size: 140,
      },
      {
        header: '규격',
        accessorKey: 'SIZE_DES',
        size: 140,
      },
      {
        header: '수량',
        accessorKey: 'QTY',
        size: 48,
      },
      {
        header: '단가',
        accessorKey: 'PRICE',
        size: 53,
        cell: (price) => formatCurrency(price.getValue() as number),
      },
      {
        header: '공급가액',
        accessorKey: 'SUPPLY_AMT',
        size: 73,
        cell: (supplyAmt) => formatCurrency(supplyAmt.getValue() as number),
      },
      {
        header: '납기일자',
        accessorKey: 'TIME_DATE',
        size: 140,
        cell: (timeDate) =>
          formatEightDigitStringToSlashForm(timeDate.getValue() as string),
      },
      {
        header: '주문방법',
        accessorKey: 'ORDER_TYPE',
        size: 66,
      },
      {
        header: '배송방법',
        accessorKey: 'DELIVERY_TYPE',
        size: 66,
      },
      {
        header: '주문특이사항',
        accessorKey: 'DESCRIPTION',
        size: 420,
      },
      {
        header: '적요',
        accessorKey: 'REMARKS',
        size: 420,
      },
    ],
    []
  );

  //아마 페이지네이션
  function useSkipper() {
    const shouldSkipRef = useRef(true);
    const shouldSkip = shouldSkipRef.current;

    // Wrap a function with this to skip a pagination reset temporarily
    const skip = useCallback(() => {
      shouldSkipRef.current = false;
    }, []);

    useEffect(() => {
      shouldSkipRef.current = true;
    });

    return [shouldSkip, skip] as const;
  }

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
  //~~페이지네이션

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: 'onChange',
    state: {
      expanded: true,
      pagination,
    },
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    autoResetPageIndex,
    getPaginationRowModel: getPaginationRowModel(),
    paginateExpandedRows: false,
    onPaginationChange: setPagination,
  });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Styled.PaginationButtonContainer>
          <Styled.PaginationButton
            size={28}
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <BsChevronDoubleLeft />
          </Styled.PaginationButton>
          <Styled.PaginationButton
            size={28}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <BsChevronLeft />
          </Styled.PaginationButton>
          {/* 중간인풋 */}
          <Styled.PaginationButton
            size={28}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <BsChevronRight />
          </Styled.PaginationButton>
          <Styled.PaginationButton
            size={28}
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
          >
            <BsChevronDoubleRight />
          </Styled.PaginationButton>
        </Styled.PaginationButtonContainer>
        <div
          style={{
            alignContent: 'center',
            marginLeft: '20px',
            marginRight: '40px',
          }}
        >
          {table.getPageCount()}페이지 중{' '}
          {table.getState().pagination.pageIndex + 1}페이지
        </div>
        <div className='flex-center'>페이지당 표시 데이터 수</div>
        <select
          id={'row-count'}
          value={pagination.pageSize}
          onChange={(e) => {
            setPagination((prev) => ({
              pageIndex: prev.pageIndex,
              pageSize: Number(e.target.value),
            }));
            table.firstPage();
          }}
          style={{ marginLeft: '14px' }}
        >
          <option key={'10'} value={10}>
            10개
          </option>
          <option key={'20'} value={20}>
            20개
          </option>
          <option key={'30'} value={30}>
            30개
          </option>
          <option key={'40'} value={40}>
            40개
          </option>
          <option key={'50'} value={50}>
            50개
          </option>
        </select>
      </div>

      <Styled.TableContainer>
        <Styled.TableOptionButton onClick={() => setShowConfig(!showConfig)}>
          설정
        </Styled.TableOptionButton>
        <TableConfig
          show={showConfig}
          Header_Overflow_Type={headerOverflowType}
          setHeaderOverflowType={(value: 'hide' | 'wrap') =>
            setHeaderOverflowType(value)
          }
          Body_Overflow_Type={bodyOverflowType}
          setBodyOverflowType={(value: 'hide' | 'wrap') =>
            setBodyOverflowType(value)
          }
          Body_Align_Hori_Type={bodyAlignHoriType}
          setBodyAlignHoriType={(value: 'left' | 'center' | 'right') =>
            setBodyAlignHoriType(value)
          }
          Cell_Hori_Padd_Value={cellHoriPaddValue}
          setCellHoriPaddValue={(value: number) => setCellHoriPaddValue(value)}
          Body_Vert_Padd_Value={bodyVertPaddValue}
          setBodyVertPaddValue={(value: number) => setBodyVertPaddValue(value)}
        />
        <Styled.TableDiv
          {...{
            style: {
              ...columnSizeVars,
              width: table.getTotalSize(),
            },
          }}
          $headOverflowType={headerOverflowType}
          $bodyOverflowType={bodyOverflowType}
          $bodyAlignHoriType={bodyAlignHoriType}
          $cellHoriPaddValue={cellHoriPaddValue}
          $bodyVertPaddValue={bodyVertPaddValue}
        >
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <Styled.ThDiv {...{ key: headerGroup.id }}>
                {headerGroup.headers.map((headerCell) =>
                  headerCell.isPlaceholder ? null : (
                    <Styled.ThCell
                      {...{
                        key: headerCell.id,
                        style: {
                          width: `calc(var(--header-${headerCell?.id}-size) * 1px)`,
                        },
                        className: 'th',
                      }}
                    >
                      {headerCell.isPlaceholder
                        ? null
                        : flexRender(
                            headerCell.column.columnDef.header,
                            headerCell.getContext()
                          )}
                      <Styled.ResizeHandler
                        {...{
                          onMouseDown: headerCell.getResizeHandler(),
                          className: `resizer ${
                            headerCell.column.getIsResizing()
                              ? 'isResizing'
                              : ''
                          }`,
                        }}
                      />
                    </Styled.ThCell>
                  )
                )}
              </Styled.ThDiv>
            );
          })}
          {table.getRowModel().rows.map((row) => (
            <>
              <Styled.TdDiv
                {...{
                  key: row.id,
                  style: {
                    // 전체 배경 색 변경 (변경된 필드가 있으면)
                    backgroundColor:
                      (row.original.changedOrderFields &&
                        row.original.changedOrderFields.length > 0) ||
                      row.original.subRows?.some(
                        (subRow) =>
                          subRow.changedItemFields &&
                          subRow.changedItemFields?.length > 0
                      )
                        ? theme.colors.lime
                        : 'transparent',
                  },
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const isMainRow = row.depth === 0;

                  // 서브로우에서 각 셀의 변경된 필드 확인 (셀이 속한 서브로우마다)
                  let subRowChangedFields = null;
                  if (!isMainRow) {
                    // 메인로우가 아닐 경우, 자기 자신에서만 변경된 필드를 찾음
                    subRowChangedFields = row.original.changedItemFields
                      ? row.original.changedItemFields.find(
                          (field) => mapKey(field) === cell.column.id
                        )
                      : null;
                  } else {
                    // 서브로우에서 찾는 경우
                    subRowChangedFields = row.original.subRows
                      ? row.original.subRows.reduce(
                          (acc, subRow, subRowIndex) => {
                            if (subRow.changedItemFields) {
                              if (
                                subRow.changedItemFields.find(
                                  (field) => mapKey(field) === cell.column.id
                                )
                              ) {
                                acc[subRowIndex] = true; // 해당 서브로우의 셀이 변경되었음을 표시
                              }
                            }
                            return acc;
                          },
                          {} as Record<number, boolean>
                        )
                      : null;
                  }

                  // 메인로우에서 변경된 필드 확인 (주문 필드 변경)
                  const isHighlighted =
                    (isMainRow &&
                      row.original.changedOrderFields &&
                      row.original.changedOrderFields.find(
                        (field) => mapKey(field) === cell.column.id
                      )) || // 메인로우의 changedOrderFields
                    (subRowChangedFields && subRowChangedFields[row.index]); // 서브로우의 changedItemFields와 일치하는 셀

                  // 커스텀 스타일 (변경된 셀에 하이라이트 적용)
                  return (
                    <Styled.TdCell
                      {...{
                        key: cell.id,
                        className: 'td',
                        style: {
                          // 해당 셀이 변경되었으면 배경 색을 노란색으로 하이라이트
                          backgroundColor: isMainRow
                            ? isHighlighted
                              ? 'rgb(253 255 85 / 26%)'
                              : // : '#3535350a'
                                theme.colors.white_odd
                            : 'transparent',
                          width: `calc(var(--header-${cell.column.id}-size) * 1px)`, // 셀 크기
                        },
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Styled.TdCell>
                  );
                })}
              </Styled.TdDiv>
            </>
          ))}
        </Styled.TableDiv>
      </Styled.TableContainer>
    </>
  );
};
